import { useEffect, useRef } from "react";
import { buildAuthHeaders } from "../services/authTokenStore";

/**
 * useStompTopic
 * Manages STOMP topic subscription lifecycle. Subscribes when enabled and client is connected,
 * and unsubscribes automatically on dependency changes or unmount.
 *
 * @param {Object} params
 * @param {import('@stomp/stompjs').Client} params.client - Connected STOMP client instance.
 * @param {string} params.destination - Topic destination string (e.g., `/topic/123`).
 * @param {(frame: any) => void} params.onMessage - Callback invoked for each received frame.
 * @param {boolean} [params.enabled=true] - Whether subscription should be active.
 * @param {string} [params.debugLabel] - Optional label for debug logging.
 */
export default function useStompTopic({ client, destination, onMessage, enabled = true, debugLabel }) {
  const subRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  const debugLabelRef = useRef(debugLabel);
  const hasMessageHandler = typeof onMessage === 'function';

  onMessageRef.current = onMessage;
  debugLabelRef.current = debugLabel;

  useEffect(() => {
    if (!enabled || !client || !client.connected || !destination || !hasMessageHandler) {
      return undefined;
    }
    // try {
    //   // eslint-disable-next-line no-console
    //   if (debugLabel) console.log('[DEBUG_LOG] STOMP subscribe', { destination, debugLabel });
    // } catch (_) {}

    const subscription = client.subscribe(destination, (frame) => {
      if (typeof onMessageRef.current === 'function') {
        onMessageRef.current(frame);
      }
    }, buildAuthHeaders());
    subRef.current = subscription;

    return () => {
      try {
        if (subRef.current) {
          // eslint-disable-next-line no-console
          if (debugLabelRef.current) {
            console.log('[DEBUG_LOG] STOMP unsubscribe', { destination, debugLabel: debugLabelRef.current });
          }
          subRef.current.unsubscribe();
        }
      } catch (_) {
        // ignore
      } finally {
        if (subRef.current === subscription) subRef.current = null;
      }
    };
  }, [client, destination, enabled, hasMessageHandler]);
}
